import { Controller } from "@hotwired/stimulus";
import * as bootstrap from "bootstrap";

export default class extends Controller {
  static values = {
    hosts: String,
    center: String,
  };

  connect() {
    if (this.element.dataset.initialized === "true") return;
    if (typeof L === "undefined") return;

    const hosts = this.parseJson(this.hostsValue);
    if (hosts.length === 0) return;

    const center = this.parseJson(this.centerValue);
    const map = L.map(this.element);

    this.element.addEventListener("click", this.handlePopupClick);
    document.addEventListener("turbo:frame-load", this.handleFrameLoad);

    L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", {
      maxZoom: 19,
      attribution: "&copy; OpenStreetMap contributors",
    }).addTo(map);

    const bounds = [];

    hosts.forEach((host) => {
      const icon = this.markerIcon(host.severity_color);
      const marker = L.marker([host.latitude, host.longitude], { icon }).addTo(
        map
      );

      marker.bindPopup(this.popupHtml(host));
      marker.on("click", () => this.highlightRow(host.row_id, host.page));
      marker.on("popupclose", () => this.clearHighlight());
      bounds.push([host.latitude, host.longitude]);
    });

    if (bounds.length > 1) {
      map.fitBounds(bounds, { padding: [16, 16] });
    } else if (center.latitude && center.longitude) {
      map.setView([center.latitude, center.longitude], 14);
    } else {
      map.setView(bounds[0], 14);
    }

    this.element.dataset.initialized = "true";
  }

  disconnect() {
    this.element.removeEventListener("click", this.handlePopupClick);
    document.removeEventListener("turbo:frame-load", this.handleFrameLoad);
  }

  highlightRow(rowId, page) {
    document
      .querySelectorAll("tr.table-active")
      .forEach((row) => row.classList.remove("table-active"));

    const row = document.getElementById(rowId);
    if (!row) {
      if (page) this.visitPage(page, { highlightRowId: rowId });
      return;
    }

    row.classList.add("table-active");
    row.scrollIntoView({ block: "center", behavior: "smooth" });
  }

  handlePopupClick = (event) => {
    const link = event.target.closest("[data-zabbix-hosts-modal]");
    if (!link) return;

    event.preventDefault();

    const rowId = link.getAttribute("data-zabbix-hosts-modal");
    const page = link.getAttribute("data-zabbix-hosts-page");

    if (!document.getElementById(rowId)) {
      this.showPopupLoading(link);
    }

    this.openModal(rowId, page);
  };

  clearHighlight() {
    document
      .querySelectorAll("tr.table-active")
      .forEach((row) => row.classList.remove("table-active"));
  }

  popupHtml(host) {
    const safeLabel = host.modal_label || "View details";
    return `<div><strong>${this.escapeHtml(
      host.name
    )}</strong><br><a href="#" data-zabbix-hosts-modal="${this.escapeHtml(
      host.row_id
    )}" data-zabbix-hosts-page="${this.escapeHtml(
      host.page
    )}">${this.escapeHtml(safeLabel)}</a></div>`;
  }

  openModal(rowId, page) {
    const row = document.getElementById(rowId);
    if (!row) {
      if (page) this.visitPage(page, { modalRowId: rowId });
      return;
    }

    const modalId = row.dataset.modalId;
    if (!modalId) return;

    const modalEl = document.getElementById(modalId);
    if (!modalEl) return;

    const modal = bootstrap.Modal.getOrCreateInstance(modalEl);
    modal.show();
    this.clearPopupLoading();
  }

  showPopupLoading(link) {
    if (link.dataset.loading === "true") return;
    link.dataset.originalLabel = link.innerHTML;
    link.dataset.loading = "true";
    link.classList.add("disabled");
    link.setAttribute("aria-busy", "true");
    link.innerHTML = `<span class="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>Loading...`;
  }

  clearPopupLoading() {
    document
      .querySelectorAll("[data-zabbix-hosts-modal][data-loading='true']")
      .forEach((link) => {
        link.classList.remove("disabled");
        link.removeAttribute("aria-busy");
        link.innerHTML = link.dataset.originalLabel || link.innerHTML;
        delete link.dataset.loading;
        delete link.dataset.originalLabel;
      });
  }

  visitPage(page, { modalRowId = null, highlightRowId = null } = {}) {
    if (!page) return;

    const url = new URL(window.location.href);
    const targetPage = page.toString();

    url.searchParams.set("page", targetPage);
    this.storePendingAction({ modalRowId, highlightRowId });

    if (window.Turbo && window.Turbo.visit) {
      window.Turbo.visit(url.toString(), { frame: "zabbix_hosts_list" });
    } else {
      window.location.assign(url.toString());
    }
  }

  handleFrameLoad = (event) => {
    if (event.target && event.target.id === "zabbix_hosts_list") {
      this.clearPopupLoading();
    }
  };

  storePendingAction({ modalRowId = null, highlightRowId = null } = {}) {
    if (!modalRowId && !highlightRowId) return;
    const payload = { modalRowId, highlightRowId };
    try {
      sessionStorage.setItem("zabbixHostsPending", JSON.stringify(payload));
    } catch (_error) {}
  }

  markerIcon(color) {
    const safeColor = color || "#97AAB3";
    const html = `<span style="display:block;width:18px;height:18px;border-radius:50%;background:${safeColor};border:2px solid #fff;box-shadow:0 0 0 1px rgba(0,0,0,0.25);"></span>`;

    return L.divIcon({
      className: "zabbix-host-marker",
      html,
      iconSize: [18, 18],
      iconAnchor: [9, 9],
      popupAnchor: [0, -8],
    });
  }

  parseJson(value) {
    if (!value) return [];

    try {
      return JSON.parse(value);
    } catch (error) {
      return [];
    }
  }

  escapeHtml(value) {
    if (!value) return "";

    const span = document.createElement("span");
    span.textContent = value.toString();
    return span.innerHTML;
  }
}
